<div class="terms-page">

  <header class="terms-header">
    <div class="terms-header__text">
      <span class="terms-header__eyebrow">{{ mode === 'category' ? 'Category' : 'Provider' }}</span>
      <h1 class="terms-header__title">{{ pageTitle }}</h1>
      <p class="terms-header__subtitle">
        {{ mode === 'category'
          ? 'Manage category-specific terms. General terms are shown for reference and edited separately.'
          : 'Define provider-wide terms that apply across all freight categories.' }}
      </p>
    </div>
    <div class="terms-header__actions">
      <button *ngIf="mode === 'category'" type="button" class="btn btn-outline-primary terms-header__view"
        (click)="openCombinedView()">
        <i class="bi bi-eye"></i>
        <span>View Combined Terms</span>
      </button>
      <button type="button" class="btn btn-light terms-header__back" (click)="goTo('/instant-rate-manager')">
        <i class="bi bi-arrow-left"></i>
        <span>Back</span>
      </button>
    </div>
  </header>

  <div *ngIf="isLoading" class="terms-loading">
    <div class="spinner-border text-primary" role="status"></div>
    <p>Loading terms and conditions...</p>
  </div>

  <ng-container *ngIf="!isLoading">

    <!-- Global reference (category mode) -->
    <aside *ngIf="mode === 'category'" class="terms-global-ref">
      <button type="button" class="terms-global-ref__toggle" (click)="toggleGlobalReference()"
        [attr.aria-expanded]="showGlobalReference">
        <div class="terms-global-ref__toggle-text">
          <i class="bi bi-globe2"></i>
          <span>General Terms & Conditions</span>
        </div>
        <i class="bi" [class.bi-chevron-up]="showGlobalReference" [class.bi-chevron-down]="!showGlobalReference"></i>
      </button>

      <div class="terms-global-ref__body" [class.terms-global-ref__body--open]="showGlobalReference">
        <div *ngIf="globalSections.length === 0" class="terms-global-ref__empty">
          <p>No general terms have been set yet.</p>
          <button type="button" class="btn btn-outline-primary btn-sm" (click)="goToGeneralTerms()">
            Set General Terms
          </button>
        </div>

        <div *ngIf="globalSections.length > 0" class="terms-global-ref__content">
          <div class="terms-global-ref__actions">
            <span class="terms-global-ref__badge">Read only</span>
            <button type="button" class="btn btn-link btn-sm p-0" (click)="goToGeneralTerms()">Edit general terms</button>
          </div>
          <div class="terms-global-ref__scroll">
            <div *ngFor="let section of globalSections" class="terms-global-ref__section">
              <h2 class="terms-global-ref__heading">{{ section.section_title }}</h2>
              <div class="terms-global-ref__html" [innerHTML]="section.section_content"></div>
            </div>
          </div>
          <p *ngIf="globalSections.length > 2" class="terms-global-ref__overflow-hint">
            Showing up to 2 sections. Scroll for {{ globalSections.length - 2 }} more, or use View Combined Terms.
          </p>
        </div>
      </div>
    </aside>

    <!-- List + Editor -->
    <div class="terms-workspace">

      <!-- Left: section list -->
      <div class="terms-list"
        [class.terms-list--hidden-mobile]="editingSection && !isCreatingNew">
        <div class="terms-list__header">
          <h2 class="terms-list__title">
            {{ listTitle }}
            <span class="terms-list__count">{{ sections.length }}</span>
          </h2>
          <button type="button" class="btn btn-primary terms-list__add" (click)="openCreateModal()">
            <i class="bi bi-plus-lg"></i>
            <span>Add New Section</span>
          </button>
        </div>

        <div *ngIf="sections.length === 0" class="terms-list__empty">
          <i class="bi bi-file-text"></i>
          <p>No terms found.</p>
          <span>Click "Add New Section" to get started.</span>
        </div>

        <div class="terms-list__items">
          <button type="button" *ngFor="let section of sections; let i = index"
            class="terms-list__item"
            [class.terms-list__item--active]="selectedSectionIndex === i && !isCreatingNew"
            (click)="selectSection(i)">
            <div class="terms-list__item-icon">
              <i class="bi bi-file-text"></i>
            </div>
            <span class="terms-list__item-title">{{ section.section_title }}</span>
            <span *ngIf="mode === 'category' && section.is_independent" class="badge bg-secondary-subtle text-secondary">
              Independent
            </span>
            <button type="button" class="terms-list__item-delete" (click)="deleteSection(i, $event)" aria-label="Delete term">
              <i class="bi bi-trash"></i>
            </button>
          </button>
        </div>
      </div>

      <!-- Right: section editor -->
      <div class="terms-editor-panel"
        [class.terms-editor-panel--hidden-mobile]="!editingSection && !isCreatingNew">

        <div *ngIf="!editingSection && !isCreatingNew" class="terms-editor-panel__placeholder">
          <div class="terms-editor-panel__placeholder-icon">
            <i class="bi bi-file-text"></i>
          </div>
          <h3>No Term Selected</h3>
          <p>Select a term from the list to view or edit details.</p>
        </div>

        <div *ngIf="editingSection && !isCreatingNew" class="terms-editor-card">
          <div class="terms-editor-card__header">
            <button type="button" class="terms-editor-card__back d-md-none" (click)="cancelEdit()">
              <i class="bi bi-arrow-left"></i>
            </button>
            <h2 class="terms-editor-card__title">Term Details</h2>
          </div>

          <form class="terms-form" (submit)="saveSection(); $event.preventDefault()">
            <div class="terms-form__field">
              <label class="terms-form__label" for="sectionTitle">Title</label>
              <input id="sectionTitle" type="text" class="form-control terms-form__input"
                [(ngModel)]="editingSection.section_title" name="sectionTitle"
                [class.is-invalid]="isSubmitted && !editingSection.section_title.trim()">
              <small *ngIf="isSubmitted && !editingSection.section_title.trim()" class="text-danger">Title is required</small>
            </div>

            <div class="terms-form__field">
              <label class="terms-form__label" for="sectionContent">Term Details</label>
              <div class="terms-editor-wrap"
                [class.summernote-invalid]="isSubmitted && isContentEmpty(editingSection.section_content)">
                <textarea id="sectionContent" appSummernote [height]="editorHeight"
                  [(ngModel)]="editingSection.section_content" name="sectionContent"></textarea>
              </div>
              <small *ngIf="isSubmitted && isContentEmpty(editingSection.section_content)" class="text-danger">
                Term details are required.
              </small>
            </div>

            <div *ngIf="mode === 'category'" class="terms-form__field">
              <div class="form-check">
                <input id="sectionIndependent" type="checkbox" class="form-check-input"
                  [(ngModel)]="editingSection.is_independent" name="sectionIndependent">
                <label class="form-check-label" for="sectionIndependent">
                  Keep this as a separate independent term
                </label>
              </div>
              <small class="text-muted d-block mt-1">
                Independent terms are shown separately to customers and excluded from the combined terms view.
              </small>
            </div>

            <div class="terms-form__actions">
              <button type="button" class="btn btn-light" (click)="cancelEdit()">Cancel</button>
              <button type="submit" class="btn btn-primary terms-form__save" [disabled]="isSaving">
                <span *ngIf="isSaving" class="spinner-border spinner-border-sm me-2"></span>
                Save Term
              </button>
            </div>
          </form>
        </div>
      </div>

    </div>
  </ng-container>
</div>

<ng-template #termModalTemplate let-modal>
  <div class="modal-header border-0 pt-4 px-4">
    <h5 class="modal-title fw-bold text-dark">Add New Section</h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body p-4 pt-2" *ngIf="editingSection">
    <div class="terms-form__field">
      <label class="terms-form__label" for="newSectionTitle">Title</label>
      <input id="newSectionTitle" type="text" class="form-control terms-form__input"
        [(ngModel)]="editingSection.section_title" name="newSectionTitle"
        [class.is-invalid]="isSubmitted && !editingSection.section_title.trim()">
      <small *ngIf="isSubmitted && !editingSection.section_title.trim()" class="text-danger">Title is required</small>
    </div>
    <div class="terms-form__field">
      <label class="terms-form__label" for="newSectionContent">Term Details</label>
      <div class="terms-editor-wrap"
        [class.summernote-invalid]="isSubmitted && isContentEmpty(editingSection.section_content)">
        <textarea id="newSectionContent" appSummernote [height]="250"
          [(ngModel)]="editingSection.section_content" name="newSectionContent"></textarea>
      </div>
      <small *ngIf="isSubmitted && isContentEmpty(editingSection.section_content)" class="text-danger">
        Term details are required.
      </small>
    </div>
    <div *ngIf="mode === 'category'" class="terms-form__field">
      <div class="form-check">
        <input id="newSectionIndependent" type="checkbox" class="form-check-input"
          [(ngModel)]="editingSection.is_independent" name="newSectionIndependent">
        <label class="form-check-label" for="newSectionIndependent">
          Keep this as a separate independent term
        </label>
      </div>
      <small class="text-muted d-block mt-1">
        Independent terms are shown separately to customers and excluded from the combined terms view.
      </small>
    </div>
  </div>
  <div class="modal-footer border-0 p-4 pt-0 gap-2">
    <button type="button" class="btn btn-light px-4" (click)="modal.dismiss()">Cancel</button>
    <button type="button" class="btn btn-primary px-4 fw-bold" (click)="saveSection()" [disabled]="isSaving">
      <span *ngIf="isSaving" class="spinner-border spinner-border-sm me-2"></span>
      Create Section
    </button>
  </div>
</ng-template>

<ng-template #combinedViewModalTemplate let-modal>
  <div class="modal-header border-0 pt-4 px-4">
    <div>
      <h5 class="modal-title fw-bold text-dark mb-1">Combined Terms & Conditions</h5>
      <p class="text-muted small mb-0">General and category terms as shown to customers.</p>
    </div>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body p-4 pt-2 terms-combined-view">
    <div *ngIf="isViewLoading" class="terms-combined-view__loading">
      <div class="spinner-border text-primary" role="status"></div>
      <p>Loading combined terms...</p>
    </div>

    <div *ngIf="!isViewLoading && !hasCombinedViewContent" class="terms-combined-view__empty">
      <i class="bi bi-file-text"></i>
      <p>No terms and conditions have been set yet.</p>
    </div>

    <ng-container *ngIf="!isViewLoading && hasCombinedViewContent">
      <section class="terms-combined-view__group">
        <h6 class="terms-combined-view__group-title">
          <i class="bi bi-globe2"></i>
          General Terms & Conditions
        </h6>
        <div *ngIf="viewGlobalSections.length === 0" class="terms-combined-view__group-empty">
          No general terms have been set.
        </div>
        <article *ngFor="let section of viewGlobalSections" class="terms-combined-view__section">
          <h3 class="terms-combined-view__section-title">{{ section.section_title }}</h3>
          <div class="terms-combined-view__section-content" [innerHTML]="section.section_content"></div>
        </article>
      </section>

      <section class="terms-combined-view__group">
        <h6 class="terms-combined-view__group-title">
          <i class="bi bi-file-text"></i>
          {{ pageTitle }}
        </h6>
        <div *ngIf="viewCategorySections.length === 0" class="terms-combined-view__group-empty">
          No category-specific terms have been set.
        </div>
        <article *ngFor="let section of viewCategorySections" class="terms-combined-view__section">
          <h3 class="terms-combined-view__section-title">{{ section.section_title }}</h3>
          <div class="terms-combined-view__section-content" [innerHTML]="section.section_content"></div>
        </article>
      </section>
    </ng-container>
  </div>
  <div class="modal-footer border-0 p-4 pt-0">
    <button type="button" class="btn btn-light px-4" (click)="modal.dismiss()">Close</button>
  </div>
</ng-template>
